import { computed, ref, watch } from 'vue';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useDateFnsLocale } from 'shared/composables/useDateFnsLocale';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import DebtKanbanApi from 'dashboard/api/debtKanban';

// Acima deste número de etapas o trilho de pontos degrada para só o
// ordinal (PRD T3.3 secao 2, "Trilho de progresso").
const MAX_RAIL_STAGES = 8;

/**
 * Centralizes the kanban funnel/stage widget logic for the conversation
 * panel (T3.3): loading the account funnels once, loading/resetting the
 * current conversation's card, and moving the card between stages/funnels
 * with optimistic UI that actually reverts on failure (the upstream
 * assign* actions swallow the error and never revert — see PRD T3.3 secao 3,
 * "Otimismo com reversão").
 *
 * @returns {Object} state, derived labels and actions for DebtKanbanBlock.
 */
export function useDebtKanbanWidget() {
  const { t } = useI18n();
  const { dateFnsLocale } = useDateFnsLocale();
  const { accountId } = useAccount();
  const currentChat = useMapGetter('getSelectedChat');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  // currentChat.id é o display_id da conversa (_conversation.json.jbuilder:29),
  // que é exatamente o parâmetro que o endpoint do card espera.
  const conversationId = computed(() => currentChat.value?.id);

  const isFeatureEnabled = computed(() => {
    if (!accountId.value) return false;
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.DEBT_KANBAN_BOARD
    );
  });

  const funnels = ref([]);
  const funnelsLoaded = ref(false);
  let funnelsRequest = null;

  const card = ref(null);
  const isLoadingCard = ref(false);
  const hasLoadedCard = ref(false);
  const isSubmitting = ref(false);

  const isSwitchingFunnel = ref(false);
  const switchFunnelTarget = ref(null);

  const hasCard = computed(() => Boolean(card.value));

  const currentFunnel = computed(() =>
    funnels.value.find(funnel => funnel.id === card.value?.funnel?.id)
  );
  const currentFunnelStages = computed(() => currentFunnel.value?.stages || []);
  const currentStageIndex = computed(() =>
    currentFunnelStages.value.findIndex(
      stage => stage.id === card.value?.stage?.id
    )
  );

  const stageOrdinalLabel = computed(() => {
    if (!hasCard.value || currentStageIndex.value === -1) return '';
    return t('CONVERSATION.KANBAN_WIDGET.STAGE_ORDINAL', {
      position: currentStageIndex.value + 1,
      total: currentFunnelStages.value.length,
    });
  });

  const showRail = computed(
    () =>
      currentStageIndex.value !== -1 &&
      currentFunnelStages.value.length > 0 &&
      currentFunnelStages.value.length <= MAX_RAIL_STAGES
  );

  const stageOptions = computed(() =>
    currentFunnelStages.value.map(stage => ({
      id: stage.id,
      name: stage.name,
    }))
  );

  const switchFunnelOptions = computed(() =>
    funnels.value
      .filter(funnel => funnel.id !== card.value?.funnel?.id)
      .map(funnel => ({ id: funnel.id, name: funnel.name }))
  );

  // entered_stage_at chega como ISO 8601 (json.entered_stage_at sem .to_i em
  // _card.json.jbuilder), não como epoch em segundos — por isso usa-se
  // date-fns diretamente em vez do helper dynamicTime (shared/helpers/timeHelper),
  // que assume unix seconds.
  const enteredStageDate = computed(() =>
    card.value?.entered_stage_at ? new Date(card.value.entered_stage_at) : null
  );

  const currentStageSlaHours = computed(() => {
    const stage = currentFunnelStages.value.find(
      s => s.id === card.value?.stage?.id
    );
    return stage?.sla_hours ?? null;
  });

  const isSlaBreached = computed(() => {
    if (!currentStageSlaHours.value || !enteredStageDate.value) return false;
    return (
      differenceInHours(new Date(), enteredStageDate.value) >=
      currentStageSlaHours.value
    );
  });

  // O estado normal e o de alarme (SLA estourado) precisam de mais do que a
  // cor para se distinguir (WCAG 1.4.1): o alarme expõe o limite da etapa em
  // vez de repetir o mesmo texto-molde só com outra classe de cor.
  const timeInStageLabel = computed(() => {
    if (!enteredStageDate.value) return '';
    // Sem `locale` o date-fns formata sempre em inglês, o que deixaria
    // "29 days" no meio da frase traduzida.
    const time = formatDistanceToNow(enteredStageDate.value, {
      locale: dateFnsLocale.value,
    });
    if (isSlaBreached.value) {
      return t('CONVERSATION.KANBAN_WIDGET.TIME_IN_STAGE_SLA_BREACHED', {
        time,
        slaHours: currentStageSlaHours.value,
      });
    }
    return t('CONVERSATION.KANBAN_WIDGET.TIME_IN_STAGE', { time });
  });

  const ensureFunnelsLoaded = async () => {
    if (funnelsLoaded.value) return;
    if (!funnelsRequest) {
      funnelsRequest = DebtKanbanApi.funnels()
        .then(({ data }) => {
          funnels.value = data;
          funnelsLoaded.value = true;
        })
        .catch(() => {
          // Permite nova tentativa na próxima troca de conversa em vez de
          // travar o widget para sempre num account com falha transitória.
          funnelsRequest = null;
        });
    }
    await funnelsRequest;
  };

  const fetchCard = async () => {
    if (!conversationId.value) return;
    const requestConversationId = conversationId.value;
    isLoadingCard.value = true;
    try {
      const { data } = await DebtKanbanApi.card(requestConversationId);
      if (requestConversationId !== conversationId.value) return;
      card.value = data.card;
    } catch (error) {
      if (requestConversationId !== conversationId.value) return;
      card.value = null;
    } finally {
      if (requestConversationId === conversationId.value) {
        isLoadingCard.value = false;
        hasLoadedCard.value = true;
      }
    }
  };

  const closeSwitchFunnel = () => {
    isSwitchingFunnel.value = false;
    switchFunnelTarget.value = null;
  };

  const reset = () => {
    card.value = null;
    isLoadingCard.value = false;
    hasLoadedCard.value = false;
    closeSwitchFunnel();
  };

  const load = async () => {
    reset();
    if (!isFeatureEnabled.value || !conversationId.value) return;
    const requestConversationId = conversationId.value;
    await ensureFunnelsLoaded();
    if (requestConversationId !== conversationId.value) return;
    await fetchCard();
  };

  // Troca de conversa reseta o widget — sem isto o estado de uma conversa
  // vaza para a seguinte (PRD T3.3 secao 3, "Troca de conversa"; mesmo
  // padrão de DebtStateBlock.vue).
  watch(conversationId, load, { immediate: true });

  // Escreve com consequência de processo: otimista, com reversão real em
  // falha de rede e recarga (não reaplicação) em conflito de lock_version.
  // Compartilhado por moveStage e confirmSwitchFunnel para não divergir a
  // garantia entre os dois caminhos de escrita.
  const applyMove = async ({
    stageId,
    funnelId = null,
    optimisticStage,
    optimisticFunnel = null,
    successMessage,
  }) => {
    if (!card.value) return false;
    if (isSubmitting.value) return false;

    const requestConversationId = conversationId.value;
    const previousCard = card.value;

    isSubmitting.value = true;
    card.value = {
      ...previousCard,
      stage: optimisticStage,
      // O servidor reinicia a contagem de tempo-na-etapa a cada troca (mesma
      // garantia anunciada em SWITCH_FUNNEL_CONFIRM_PROMPT); sem isto o card
      // otimista ficava com o entered_stage_at da etapa anterior, o que
      // combinava o sla_hours da etapa nova com um timestamp de outra etapa
      // e tanto exibia um tempo-na-etapa errado quanto podia acender o
      // alarme de SLA falsamente assim que o movimento era aplicado.
      entered_stage_at: new Date().toISOString(),
      ...(optimisticFunnel ? { funnel: optimisticFunnel } : {}),
    };

    try {
      const { data } = await DebtKanbanApi.moveCard(previousCard.id, {
        stageId,
        funnelId,
        lockVersion: previousCard.lock_version,
      });
      if (requestConversationId !== conversationId.value) return true;
      // cards#update devolve o card ACHATADO via a partial _card (mesma
      // forma de board/index e board/column), sem envelope e sem os objetos
      // aninhados funnel/stage que este widget usa — é uma forma diferente
      // da de conversations/:id/card (GET, usado em fetchCard acima), que
      // envelopa em `card` com funnel/stage já resolvidos. Por isso só os
      // campos primitivos autoritativos são mesclados aqui; funnel/stage
      // continuam os valores otimistas já aplicados acima (que refletem o
      // alvo do movimento que acabou de ser confirmado).
      card.value = {
        ...card.value,
        id: data.id,
        lock_version: data.lock_version,
        entered_stage_at: data.entered_stage_at,
      };
      useAlert(successMessage);
      return true;
    } catch (error) {
      if (requestConversationId !== conversationId.value) return false;
      if (error?.response?.status === 409) {
        await fetchCard();
        useAlert(t('CONVERSATION.KANBAN_WIDGET.CONFLICT'));
      } else {
        card.value = previousCard;
        useAlert(t('CONVERSATION.KANBAN_WIDGET.MOVE_ERROR'));
      }
      return false;
    } finally {
      if (requestConversationId === conversationId.value) {
        isSubmitting.value = false;
      }
    }
  };

  const moveStage = stage => {
    if (!card.value || stage.id === card.value.stage.id) {
      return Promise.resolve(false);
    }
    const targetStage = currentFunnelStages.value.find(s => s.id === stage.id);
    if (!targetStage) return Promise.resolve(false);

    return applyMove({
      stageId: targetStage.id,
      optimisticStage: {
        id: targetStage.id,
        name: targetStage.name,
        slug: targetStage.slug,
        position: targetStage.position,
      },
      successMessage: t('CONVERSATION.KANBAN_WIDGET.MOVE_SUCCESS'),
    });
  };

  const startSwitchFunnel = () => {
    isSwitchingFunnel.value = true;
    switchFunnelTarget.value = null;
  };

  const cancelSwitchFunnel = () => {
    closeSwitchFunnel();
  };

  const selectSwitchFunnelTarget = funnel => {
    switchFunnelTarget.value = funnel;
  };

  const confirmSwitchFunnel = async () => {
    if (!switchFunnelTarget.value) return false;
    const targetFunnel = funnels.value.find(
      funnel => funnel.id === switchFunnelTarget.value.id
    );
    const entryStage = targetFunnel?.stages?.[0];
    if (!targetFunnel || !entryStage) return false;

    const moved = await applyMove({
      stageId: entryStage.id,
      funnelId: targetFunnel.id,
      optimisticStage: {
        id: entryStage.id,
        name: entryStage.name,
        slug: entryStage.slug,
        position: entryStage.position,
      },
      optimisticFunnel: {
        id: targetFunnel.id,
        name: targetFunnel.name,
        slug: targetFunnel.slug,
      },
      successMessage: t('CONVERSATION.KANBAN_WIDGET.SWITCH_FUNNEL_SUCCESS'),
    });
    if (moved) closeSwitchFunnel();
    return moved;
  };

  return {
    conversationId,
    isFeatureEnabled,
    funnels,
    card,
    hasCard,
    isLoadingCard,
    hasLoadedCard,
    isSubmitting,
    currentFunnel,
    currentFunnelStages,
    stageOrdinalLabel,
    showRail,
    currentStageIndex,
    stageOptions,
    switchFunnelOptions,
    timeInStageLabel,
    isSlaBreached,
    isSwitchingFunnel,
    switchFunnelTarget,
    moveStage,
    startSwitchFunnel,
    cancelSwitchFunnel,
    selectSwitchFunnelTarget,
    confirmSwitchFunnel,
    reset,
  };
}
